import type { AdminConfigItemDto, JsonValue } from '@carcheck/shared';
import type { TFunction } from 'i18next';

/**
 * Read-side display helpers for the runtime-config table (AUT-131). Pure +
 * page-local so the row + page specs share one canonical formatting.
 *
 * The wire carries NO `description` field (architect guardrail — don't
 * invent one on the DTO); the operator hint is a CLIENT-side i18n map
 * `configDescriptions.<key>` resolved at render. `descriptionKey` returns
 * that key; a key with no gloss resolves to the key itself (the caller
 * compares against the key to decide whether to render the hint line).
 *
 * AUT-284 — the gloss is no longer a hint UNDER the key, it IS the row's
 * identity. `describeConfigKey` is the single place that decides what an
 * operator reads for a key, so the table and the SEARCH agree (hiding the
 * key while still only matching on it would make the search a trap).
 */

/** i18n key for a config key's operator-hint gloss. */
export const descriptionKey = (key: string): string => `configDescriptions.${key}`;

/** i18n key for a domain group's human name (`pricing` → «Цены»). */
export const groupLabelKey = (group: string): string => `configGroups.${group}`;

export interface ConfigKeyDescription {
  /** What the operator reads: the human sentence, or the raw key if none. */
  label: string;
  /**
   * `false` when no gloss is registered and `label` fell back to the raw
   * key. A gloss-less row must still be identifiable, so the fallback is
   * the key — rendered mono, so it reads as the exception it is, and so a
   * missing translation is visible rather than a blank row.
   */
  humanised: boolean;
}

/** The human identity of a config key (AUT-284). */
export const describeConfigKey = (key: string, t: TFunction): ConfigKeyDescription => {
  const i18nKey = descriptionKey(key);
  const label = t(i18nKey);
  // i18next returns the key itself for a missing translation.
  return label === i18nKey ? { label: key, humanised: false } : { label, humanised: true };
};

/** The human name of a domain group; falls back to the raw prefix. */
export const groupLabel = (group: string, t: TFunction): string => {
  const i18nKey = groupLabelKey(group);
  const label = t(i18nKey);
  return label === i18nKey ? group : label;
};

/**
 * Format a `JsonValue` for the read-only value cell (mirrors the mockup's
 * `cfg-val`): strings are quoted, json (array/object) is compact JSON,
 * booleans + numbers stringify plainly. Used for the non-editing display
 * of number / string / json rows (boolean rows render a `<Switch>`).
 */
export const formatValue = (value: JsonValue | null): string => {
  if (value === null) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * The string a type-specific editor starts from:
 *   - `json` → pretty-printed (2-space) so the textarea is readable;
 *   - `string` → the raw string (no surrounding quotes — the text field
 *     edits the value, not its JSON encoding);
 *   - `number` → the number as text;
 *   - `boolean` → handled by the toggle, not a text seed (returns '').
 */
export const editorSeed = (item: AdminConfigItemDto): string => {
  const { value, type } = item;
  if (type === 'boolean') return '';
  if (type === 'json') return JSON.stringify(value, null, 2);
  if (type === 'string') return typeof value === 'string' ? value : String(value ?? '');
  // number
  return value === null ? '' : String(value);
};
